import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { TIPO_DE_BEM, competenciaAtual, nomeDoMes } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { reais, type BemDaLista } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { moldura } from "../estilos";
import { BemDialog, bemVazio, type BemInicial } from "./BemDialog";

const mesCurto = (c: string) => `${c.slice(5, 7)}/${c.slice(0, 4)}`;

function Acoes({ b, onEditar }: { b: BemDaLista; onEditar: () => void }) {
  const utils = trpc.useUtils();
  const feito = (msg: string) => ({
    onSuccess: () => {
      toast.success(msg);
      utils.financeiro.invalidate();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });
  const baixar = trpc.financeiro.bens.baixar.useMutation(feito("Baixa registrada: a depreciação parou."));
  const excluir = trpc.financeiro.bens.excluir.useMutation(feito("Bem excluído."));
  const acao = "text-xs font-semibold text-zinc-500 hover:text-zinc-900 dark:hover:text-foreground";
  return (
    <div className="flex shrink-0 items-center gap-3">
      <button type="button" aria-label={`Editar ${b.nome}`} onClick={onEditar} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-muted">
        <Pencil className="h-3.5 w-3.5" />
      </button>
      {b.baixadoEm ? (
        <button type="button" className={acao} onClick={() => baixar.mutate({ id: b.id, baixadoEm: null })}>
          Desfazer baixa
        </button>
      ) : (
        <button type="button" className={acao} onClick={() => window.confirm(`Dar baixa em ${b.nome} (vendido ou descartado) em ${nomeDoMes(competenciaAtual())}?`) && baixar.mutate({ id: b.id, baixadoEm: competenciaAtual() })}>
          Dar baixa
        </button>
      )}
      <button type="button" className={cn(acao, "text-red-600 hover:text-red-700")} onClick={() => window.confirm(`Excluir ${b.nome}? Use só se foi cadastrado por engano.`) && excluir.mutate({ id: b.id })}>
        Excluir
      </button>
    </div>
  );
}

/** Equipamentos e bens: a base da depreciação do DRE (fresadora, forno, scanner, computadores). */
export function BensDoLaboratorio() {
  const pode = usePermissao("finance.manage");
  const bens = trpc.financeiro.bens.listar.useQuery().data ?? [];
  const [editando, setEditando] = useState<BemInicial | null>(null);
  const mensal = bens.filter((b) => !b.baixadoEm && b.valorAtualCents > b.valorResidualCents).reduce((t, b) => t + b.mensalCents, 0);

  return (
    <section className={cn(moldura, "overflow-hidden")}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 bg-zinc-50/60 px-4 py-2.5 dark:border-border dark:bg-muted/30">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Equipamentos e bens</h3>
          <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">Depreciação por mês: {reais(mensal)}. Entra no DRE depois do EBITDA, sem mexer no caixa.</p>
        </div>
        {pode && (
          <button type="button" onClick={() => setEditando(bemVazio())} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline dark:text-primary">
            <Plus className="h-3.5 w-3.5" /> Novo bem
          </button>
        )}
      </header>
      {bens.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nenhum bem cadastrado. Cadastre as máquinas e os equipamentos para a depreciação entrar no DRE.</p>
      ) : (
        <ul className="divide-y divide-zinc-100 dark:divide-border/50">
          {bens.map((b) => (
            <li key={b.id} className={cn("flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5", b.baixadoEm && "opacity-60")}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-zinc-900 dark:text-foreground">{b.nome}</p>
                <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                  {TIPO_DE_BEM[b.tipo].rotulo} · comprado em {mesCurto(b.compradoEm)} por {reais(b.valorCents)} · {b.vidaUtilMeses / 12} anos
                  {b.baixadoEm && ` · baixado em ${mesCurto(b.baixadoEm)}`}
                </p>
              </div>
              <div className="text-right text-[11px] text-zinc-500 dark:text-muted-foreground">
                <p>
                  <span className="tabular-nums font-semibold text-zinc-900 dark:text-foreground">{reais(b.mensalCents)}</span>/mês
                </p>
                <p>vale hoje {reais(b.valorAtualCents)}</p>
              </div>
              {pode && <Acoes b={b} onEditar={() => setEditando({ id: b.id, nome: b.nome, tipo: b.tipo, valorCents: b.valorCents, compradoEm: b.compradoEm, vidaUtilMeses: b.vidaUtilMeses, valorResidualCents: b.valorResidualCents, lancamentoId: b.lancamentoId })} />}
            </li>
          ))}
        </ul>
      )}
      <BemDialog inicial={editando} onFechar={() => setEditando(null)} />
    </section>
  );
}
